// markdownPreprocessor.ts
// ------------------------------------------------------------
// Preprocess messy markdown text into clean, well-structured markdown
// Usage:
//   import { preprocessMarkdown } from "./markdownPreprocessor";
//   const clean = preprocessMarkdown(rawString);
//
// Dependencies:
//   npm install unified remark-parse remark-stringify remark-gfm unist-util-visit
// ------------------------------------------------------------

import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import { visit } from "unist-util-visit";
import type { Root, Paragraph, List, ListItem, Heading } from "mdast";
import remarkStringify, { Options as RemarkStringifyOptions } from "remark-stringify";

export function preprocessMarkdown(rawContent: string): string {
  console.log("this is rawcontent: ", rawContent)
  const pre = safePreclean(rawContent);

  const file = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(listAndHeaderTransformer) // custom plugin
    .use(remarkStringify, {
      fences: true,
      bullet: "-",
      listItemIndent: "one",
    } as RemarkStringifyOptions)
    .processSync(pre);

    let processed = String(file).trim();

  processed = postProcessMarkdown(processed);

  console.log("*****************************************------------*********************************")
  console.log("this is file: ", processed)

  // return String(file).trim();
  return processed;
}

/* -----------------------
   Stage 1: Safe pre-clean
   ------------------------ */
function safePreclean(s: string): string {
  let t = s;

  // 1️⃣ Ensure headings start on new lines (### heading)
  t = t.replace(/([^\n])###/g, "$1\n\n###");

  // 2️⃣ Add line breaks after bold sections stuck to following text
  t = t.replace(/(\*\*[^*]+\*\*)(?=\S)/g, "$1\n\n");

  // 3️⃣ Fix bold markers broken by newlines
  t = t.replace(/\*\*\s*\n\s*/g, "** ");

  // 4️⃣ Normalize "Summary Table" markers properly, ensure heading on new line
  t = t.replace(/---\s*\*\*Summary Table:\s*([^*]+)\*\*/gi, "\n\n### Summary Table: $1");

  // 6️⃣ Collapse extra blank lines (3 or more → 2)
  t = t.replace(/\n{3,}/g, "\n\n");

  // 7️⃣ Split double pipes '||' inside table rows into separate lines
  t = t.replace(/(?<=\|[^\n]*)\|\|(?=[^\n]*\|)/g, "|\n|");

  return t.trim();
}

function parseInlineMarkdown(markdown: string) {
  const tree = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .parse(markdown) as Root;

  // Return all inline children of all paragraphs
  const children = tree.children.flatMap((node) => {
    if (node.type === 'paragraph') {
      return node.children;
    }
    return [];
  });

  return children;
}


/* -----------------------
   Stage 2: AST transformer
   ------------------------ */
function listAndHeaderTransformer() {
  return (tree: Root) => {
    const replacements: Array<{ parent: any; index: number; replacement: any }> = [];

    visit(tree, "paragraph", (node: Paragraph, index: number | undefined, parent: any) => {
      if (typeof index !== "number" || !parent) return;

      const text = node.children
        .map((c: any) => ("value" in c ? String(c.value) : ""))
        .join("");

      // Case 1: Numbered + bold title inline
      const mNumberBold = text.match(/^\s*(\d+)\.\s*\*\*([^*]+)\*\*\s*([\s\S]*)$/);
      if (mNumberBold) {
        const startNum = parseInt(mNumberBold[1], 10);
        const title = mNumberBold[2].trim();
        const rest = (mNumberBold[3] || "").trim();

        // Split rest by lines that start with '-' and parse each line as inline markdown
        const restNodes: Paragraph[] = rest
          ? rest.split(/\n\s*-\s+/) // split by dash
                .filter(line => line.trim())
                .map(line => ({
                  type: 'paragraph',
                  children: parseInlineMarkdown(line.trim())
                }))
          : [];

        const listNode: List = {
          type: "list",
          ordered: true,
          start: startNum,
          spread: false,
          children: [
            {
              type: "listItem",
              spread: false,
              children: [
                { type: "paragraph", children: [{ type: "strong", children: [{ type: "text", value: title }] }] },
                ...restNodes
              ],
            } as ListItem,
          ],
        };

        replacements.push({ parent, index, replacement: listNode });
        return;
      }

      // Case 2: Simple numbered paragraph (no bold)
      const mNumberOnly = text.match(/^\s*(\d+)\.\s+([\s\S]+)$/);
      if (mNumberOnly) {
        const startNum = parseInt(mNumberOnly[1], 10);
        const rest = mNumberOnly[2].trim();

        const listNode: List = {
          type: "list",
          ordered: true,
          start: startNum,
          spread: false,
          children: [
            {
              type: "listItem",
              spread: false,
              children: [{ type: "paragraph", children: [{ type: "text", value: rest }] }],
            } as ListItem,
          ],
        };

        replacements.push({ parent, index, replacement: listNode });
        return;
      }

      // Case 3: Heading stuck inside paragraph
      const mHeading = text.match(/^\s*#{1,6}\s*([\s\S]+)$/);
      if (mHeading) {
        // const level = Math.min(6, (text.match(/^#+/)?.[0].length || 3));
        const level = Math.min(6, (text.match(/^#+/)?.[0].length || 3)) as 1 | 2 | 3 | 4 | 5 | 6;
        const rest = mHeading[1].trim();

        const headingNode: Heading = {
          type: "heading",
          depth: level,
          children: [{ type: "text", value: rest }],
        };

        replacements.push({ parent, index, replacement: headingNode });
        return;
      }
    });

    // Apply queued replacements
    for (let i = replacements.length - 1; i >= 0; i--) {
      const { parent, index, replacement } = replacements[i];
      parent.children.splice(index, 1, replacement);
    }
  };
}


/* ------
  Stage 3: Post processing
-----*/
function postProcessMarkdown(s: string): string {
  let t = s;

  // 0️⃣ Insert line break when colon or dot is immediately followed by dash (":-" or ".-")
  // This avoids breaking normal text like "milk- or"
  t = t.replace(/([:.])\s*-/g, "$1\n-");

  // 1️⃣ Add line break after table ends before next content (detect table followed by non-newline)
  t = t.replace(/(\|[\s\S]*?\|)\*\*Action Steps:\*\*/g, "$1\n\n**Action Steps:**");

  // 2️⃣ Ensure every dash '-' for lists starts on a new line
  // Only insert newline if dash is NOT preceded by a dot and space (numeric lists like 1. Item)
  // t = t.replace(/(?<!\.\s)\s*-\s+/g, "\n- ");
  t = t.replace(/([:.])\s*-/g, "$1\n-");

  // 2a️⃣ NEW RULE: Insert a space after dash if immediately followed by asterisks
  t = t.replace(/-(\*\*)/g, "- $1");

  // 3️⃣ Ensure a line break after a dot before bold or heading (**Word**)
  // But skip numeric lists like 1. **Item**
  t = t.replace(/(?<!\d)\.([*]{2}[A-Z])/g, ".\n$1");

  // 4️⃣ Collapse excessive blank lines to max 2
  t = t.replace(/\n{3,}/g, "\n\n");

  // 5️⃣ If a heading (#, ##, ###, etc.) has a '|' in the same line, insert a newline before the first '|'
  t = t.replace(/^(#{1,6}[^\n]*?)\s*(\|)/gm, "$1\n$2");

  // 6️⃣ Remove any backslashes before pipes in tables
  t = t.replace(/\\\|/g, '|');

  // 7️⃣ Remove empty lines **between header and separator row**
  t = t.replace(/(\|[^\n]+\|)\n\s*\n(\|[-\s|:]+\|)/g, "$1\n$2");

  // 8️⃣ Fix escaped bold markers in headings and list items
  t = t.replace(/\\\*\*/g, '**');

  // 8a️⃣ Add line break for punctuation + bold + dash
  t = t.replace(/([.:])(\*\*)\s*-/g, "$1$2\n-");

  // Remove leading --- before bold summary headings
  // Remove any leading dashes (--- or -- or -) and optional spaces before bold summary headings
  t = t.replace(/^-+\s*\*\*(In summary:[^*]*)\*\*/m, "**$1**");

  // Insert line breaks between adjacent Markdown links
  t = t.replace(/(\]\s*)\[(?=[^\]]+\])/g, "$1\n[");

  // Insert a line break before "If you need..." when it follows a link
  t = t.replace(/(\]\s*)(If you need)/g, "$1\n$2");

  // Insert line break after bold ending followed by dash and link
  t = t.replace(/(\*\*[^*]+\*\*):\s*-\s*(?=\[)/g, "$1:\n- ");

  // Add line break between period and asterisk only if the asterisk is followed by non-space
  t = t.replace(/([a-zA-Z])\.([*])(\S)/g, "$1.\n$2$3");
  
  // 9️⃣ Insert line breaks between consecutive links or link followed by text
  // Matches a closing bracket ']' immediately followed by '-' or '[' or text
  t = t.replace(/(\])(-|\[)/g, "$1\n$2");

  // Also handle a closing bracket followed directly by uppercase text (no space)
  t = t.replace(/(\])([A-Z])/g, "$1\n$2");


  return t.trim();
}

